import React, { useEffect, useMemo, useState } from 'react'
import { loadTalents, setTalentSortOrder } from '../../../utils/Network';
import { getDefaultName, talentImageSrc } from '../../../utils';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import TalentCardsWidget from './TalentCardsWidget';

const DEFAULT_IMAGE = '/assets/images/default.png';

const getUserTalentsCount = (item) => {
    if (!item) return 0;
    const raw = item.count_of_usertalents ?? 0;
    const count = Number(raw);
    return Number.isFinite(count) ? count : 0;
};

/** Build nested copies (loose parent match) without mutating the flat API list */
function buildTalentSubtrees(orderedRoots, allTalents) {
    const attach = (item) => {
        const subs = allTalents.filter((x) => x.parent == item.id);
        return {
            ...item,
            childs: subs.map((s) => attach(s)),
        };
    };
    return orderedRoots.map((item) => attach(item));
}

export default function TalentChildrenWidget({ talentId }) {
    const [allTalents, setAllTalents] = useState([]);
    const [children, setChildren] = useState([]);
    const [showSortDialog, setShowSortDialog] = useState(false);
    const [gridView, setGridView] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});

    useEffect(() => {
        if (!talentId) return;
        loadTalents().then((talents) => {
            const list = talents || [];
            setAllTalents(list);
            setChildren(list.filter((item) => item.parent == talentId));
        });
    }, [talentId]);

    const hierarchyRoots = useMemo(
        () => buildTalentSubtrees(children, allTalents),
        [children, allTalents]
    );

    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination || destination.index === source.index) return;

        const temp = [...children];
        const [moved] = temp.splice(source.index, 1);
        temp.splice(destination.index, 0, moved);
        setChildren(temp);
        setTalentSortOrder(temp.map((item) => item.id));
    };

    const toggleView = (e) => {
        e.preventDefault();
        setGridView((v) => !v);
    };

    const toggleAccordionItem = (itemId, e) => {
        e.preventDefault();
        setExpandedItems((prev) => ({
            ...prev,
            [itemId]: !prev[itemId],
        }));
    };

    const getTalentImage = (item) => talentImageSrc(item, DEFAULT_IMAGE);

    const renderTalentNode = (item, depth = 0) => {
        const hasChildren = item.childs && item.childs.length > 0;
        const isExpanded = !!expandedItems[item.id];
        const depthClass =
            depth >= 1 ? ' talent-hierarchy-item--third-level' : '';

        return (
            <li
                key={`talent-node-${item.id}`}
                className={`talent-hierarchy-item${depthClass}`}
                style={{ marginLeft: depth * 3, display: 'block' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div>
                        <a className="talent-hierarchy-child-link" href={`/cms/talents/${item.id}`}>
                            {getDefaultName(item.names)}
                        </a>{' '}
                        <a
                            className="talent-count-inline talent-count-inline--link"
                            href={`/cms/talent/${item.id}`}
                            title="View participants"
                        >
                            ({getUserTalentsCount(item)})
                        </a>
                    </div>
                    <div className="pull-right" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {hasChildren && (
                            <button
                                type="button"
                                className="btn btn-cm btn-xs"
                                title={isExpanded ? 'Hide childs' : 'Show childs'}
                                onClick={(e) => toggleAccordionItem(item.id, e)}
                            >
                                <i className={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                            </button>
                        )}
                    </div>
                </div>
                {hasChildren && isExpanded && (
                    <ul className="list-unstyled talent-hierarchy-list" style={{ width: '100%', marginTop: 8 }}>
                        {item.childs.map((child) => renderTalentNode(child, depth + 1))}
                    </ul>
                )}
            </li>
        );
    };

    return (
        <div className="">
            {children.length > 0 && (
                <div className="box-header bg-transparent with-border clearfix">
                    <h3 className="box-title"></h3>
                    <button
                        type="button"
                        title="Change View"
                        className="btn btn-cm margin-r-5 pull-right mt-2p"
                        onClick={toggleView}
                    >
                        <i className={`fa ${gridView ? 'fa-list' : 'fa-th'}`}></i>
                    </button>
                    <button
                        type="button"
                        className="btn btn-cm margin-r-5 pull-right mt-2p"
                        onClick={() => setShowSortDialog(true)}
                    >
                        <i className="fa fa-sort"></i> Sort
                    </button>
                </div>
            )}
            {gridView ? (
                <TalentCardsWidget talents={children} topLevelOnly={false} />
            ) : (
                children.length > 0 && (
                    <div className="box-body p-4 pt-0">
                        <div className="row talent-hierarchy-row">
                            {hierarchyRoots.map((item) => (
                                <div key={item.id} className="talent-hierarchy-col">
                                    <div className="talent-hierarchy-card">
                                        <div className="talent-hierarchy-header">
                                            <h4 className="talent-hierarchy-title">
                                                <a className="talent-hierarchy-parent-link" href={`/cms/talents/${item.id}`}>
                                                    {getDefaultName(item.names)}
                                                </a>{' '}
                                                <a
                                                    className="talent-count-inline talent-count-inline--link"
                                                    href={`/cms/talent/${item.id}`}
                                                    title="View participants"
                                                >
                                                    ({getUserTalentsCount(item)})
                                                </a>
                                            </h4>
                                            <div className="pull-right" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                {item.childs && item.childs.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-cm btn-xs"
                                                        title={expandedItems[item.id] ? 'Hide childs' : 'Show childs'}
                                                        onClick={(e) => toggleAccordionItem(item.id, e)}
                                                    >
                                                        <i className={`fa ${expandedItems[item.id] ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="talent-hierarchy-body">
                                            {!expandedItems[item.id] && (
                                                <div className="talent-hierarchy-body-collapsed">
                                                    <a
                                                        className="talent-hierarchy-thumb-wrap"
                                                        href={`/cms/talents/${item.id}`}
                                                        title={getDefaultName(item.names)}
                                                    >
                                                        <img
                                                            className="talent-hierarchy-thumb"
                                                            src={getTalentImage(item)}
                                                            alt={getDefaultName(item.names)}
                                                        />
                                                    </a>
                                                    <a
                                                        className="talent-card-count talent-hierarchy-body-count"
                                                        href={`/cms/talent/${item.id}`}
                                                        title="View participants"
                                                    >
                                                        <i className="fa fa-users"></i>
                                                        {getUserTalentsCount(item)}
                                                    </a>
                                                </div>
                                            )}

                                            {item.childs && item.childs.length > 0 && expandedItems[item.id] && (
                                                <ul className="list-unstyled talent-hierarchy-list">
                                                    {item.childs.map((child) => renderTalentNode(child))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}

            {showSortDialog && (
                <div className="modal fade in" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <button type="button" className="close" onClick={() => setShowSortDialog(false)}>&times;</button>
                                <h4 className="modal-title">Sort childs</h4>
                            </div>
                            <div className="modal-body no-padding">
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <table className="table table-striped cms-list-table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <Droppable droppableId="talent-children">
                                            {(dropProvided) => (
                                                <tbody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                                    {children.map((item, index) => (
                                                        <Draggable key={item.id} draggableId={`${item.id}`} index={index}>
                                                            {(provided) => (
                                                                <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                    <td>{getDefaultName(item.names)}</td>
                                                                    <td className="text-right">
                                                                        <div className="btn-group cms-row-actions">
                                                                            <a
                                                                                href={`/cms/talents/${item.id}`}
                                                                                className="btn btn-default btn-sm cms-row-action-btn"
                                                                            >
                                                                                <i className="fa fa-edit"></i>
                                                                            </a>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {dropProvided.placeholder}
                                                </tbody>
                                            )}
                                        </Droppable>
                                    </table>
                                </DragDropContext>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" onClick={() => setShowSortDialog(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
